import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType, type DragEvent, type PointerEvent as ReactPointerEvent } from 'react';
import {
  addEdge,
  applyNodeChanges,
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
  SelectionMode,
  type ReactFlowInstance,
  useEdgesState,
  useNodesState,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type NodeProps
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  ArrowTrendingUpIcon,
  Bars3BottomLeftIcon,
  BuildingOfficeIcon,
  CameraIcon,
  Cog6ToothIcon,
  CpuChipIcon,
  MapPinIcon,
  RectangleStackIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { Droplets, FlaskConical, Gauge, Maximize2, Thermometer, Waves, Zap } from 'lucide-react';
import type {
  AqlClient,
  AqlConfigurationDataset,
  AqlKit,
  AqlProject,
  AqlSensor
} from '@/app/types/aql-configuration';
import { getOperationalContextProfile, getOperationalLocationTypeLabel } from '@/app/utils/operational-context';

type ArchitectureNodeType = 'client' | 'project' | 'location' | 'zone' | 'kit' | 'camera' | 'sensor' | 'ultrasound' | 'threshold';
type ArchitectureRelation =
  | 'owns'
  | 'contains'
  | 'installed_at'
  | 'monitors'
  | 'camera_feed'
  | 'sensor_feed'
  | 'probe_feed'
  | 'alert_rule';

export interface ArchitectureGraphNodeData extends Record<string, unknown> {
  type: ArchitectureNodeType;
  title: string;
  subtitle?: string;
  meta?: string;
  status?: string;
  value: Record<string, any>;
}

export interface ArchitectureGraphNode {
  id: string;
  type: ArchitectureNodeType;
  title: string;
  position: { x: number; y: number };
  data: ArchitectureGraphNodeData;
}

export interface ArchitectureGraphEdge {
  id: string;
  source: string;
  target: string;
  relation: ArchitectureRelation;
}

interface OperationalArchitectureFlowProps {
  client: AqlClient | null;
  clients?: AqlClient[];
  allowClientCreation?: boolean;
  discardDraftNodeId?: string | null;
  dataset: AqlConfigurationDataset;
  projects: AqlProject[];
  rules: Array<Record<string, any>>;
  onOpenNode: (type: ArchitectureNodeType, value: Record<string, any>, meta?: { draftNodeId?: string }) => void;
}

const NODE_WIDTH = 300;
const NODE_HEIGHT = 126;
const READABLE_INITIAL_VIEWPORT = { x: 72, y: 58, zoom: 0.72 };
const READABLE_MIN_ZOOM = 0.52;
const READABLE_MAX_ZOOM = 1.2;
const LEVEL_X = {
  client: 0,
  project: 360,
  location: 720,
  zone: 1080,
  kit: 1540,
  device: 1900,
  rule: 2260
};

const nodeTypeLabel: Record<ArchitectureNodeType, string> = {
  client: 'Cliente',
  project: 'Projeto',
  location: 'Instalação',
  zone: 'Área operacional',
  kit: 'Edge',
  camera: 'Câmara',
  sensor: 'Sensor',
  ultrasound: 'Sonda',
  threshold: 'Regra de alertas'
};

const nodeIconMap: Record<ArchitectureNodeType, ComponentType<{ className?: string }>> = {
  client: BuildingOfficeIcon,
  project: Bars3BottomLeftIcon,
  location: MapPinIcon,
  zone: ArrowTrendingUpIcon,
  kit: CpuChipIcon,
  camera: CameraIcon,
  sensor: Cog6ToothIcon,
  ultrasound: RectangleStackIcon,
  threshold: ShieldCheckIcon
};

const nodeToneClass: Record<ArchitectureNodeType, string> = {
  client: 'border-slate-300 bg-white text-slate-900 dark:border-slate-600/70 dark:bg-slate-800/95 dark:text-slate-100',
  project: 'border-sky-500/25 bg-sky-500/10 text-sky-900 dark:border-sky-400/35 dark:bg-sky-400/12 dark:text-sky-100',
  location: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-900 dark:border-emerald-400/35 dark:bg-emerald-400/12 dark:text-emerald-100',
  zone: 'border-indigo-500/25 bg-indigo-500/10 text-indigo-900 dark:border-indigo-400/35 dark:bg-indigo-400/12 dark:text-indigo-100',
  kit: 'border-orange-500/25 bg-orange-500/10 text-orange-900 dark:border-orange-400/35 dark:bg-orange-400/12 dark:text-orange-100',
  camera: 'border-violet-500/25 bg-violet-500/10 text-violet-900 dark:border-violet-400/35 dark:bg-violet-400/12 dark:text-violet-100',
  sensor: 'border-blue-500/25 bg-blue-500/10 text-blue-900 dark:border-blue-400/35 dark:bg-blue-400/12 dark:text-blue-100',
  ultrasound: 'border-cyan-500/25 bg-cyan-500/10 text-cyan-900 dark:border-cyan-400/35 dark:bg-cyan-400/12 dark:text-cyan-100',
  threshold: 'border-rose-500/25 bg-rose-500/10 text-rose-900 dark:border-rose-400/35 dark:bg-rose-400/12 dark:text-rose-100'
};

const paletteNodeTypes: Array<{
  type: ArchitectureNodeType;
  label: string;
}> = [
  { type: 'client', label: 'Cliente' },
  { type: 'project', label: 'Projeto' },
  { type: 'location', label: 'Instalação' },
  { type: 'zone', label: 'Área' },
  { type: 'kit', label: 'Edge' },
  { type: 'camera', label: 'Câmara' },
  { type: 'sensor', label: 'Sensor' },
  { type: 'ultrasound', label: 'Sonda' },
  { type: 'threshold', label: 'Regra' }
];

const storageKeyForClient = (clientId: string | null) => `aql-config-architecture-flow:v2:${clientId ?? 'global'}:positions`;

const formatCount = (count: number, singular: string, plural: string) => `${count} ${count === 1 ? singular : plural}`;

const sensorLabelMap: Record<string, string> = {
  temperature: 'Temperatura',
  sensor_temperature: 'Temperatura',
  ph: 'pH',
  sensor_ph: 'pH',
  dissolved_oxygen: 'O2 dissolvido',
  oxygen: 'O2 dissolvido',
  sensor_dissolved_oxygen: 'O2 dissolvido',
  redox: 'Redox / ORP',
  orp: 'Redox / ORP',
  sensor_redox: 'Redox / ORP',
  salinity: 'Salinidade',
  sensor_salinity: 'Salinidade',
  water_level: 'Nível de água',
  sensor_water_level: 'Nível de água',
  level: 'Nível'
};

const sensorIconFor = (sensorType: string) => {
  const normalized = sensorType.toLowerCase();
  if (normalized.includes('temperature')) return Thermometer;
  if (normalized === 'ph' || normalized.includes('sensor_ph')) return FlaskConical;
  if (normalized.includes('dissolved_oxygen') || normalized.includes('oxygen')) return Droplets;
  if (normalized.includes('redox') || normalized.includes('orp')) return Zap;
  if (normalized.includes('water_level') || normalized === 'level') return Gauge;
  if (normalized.includes('salinity')) return Waves;
  return Cog6ToothIcon;
};

const ruleSensorId = (rule: Record<string, any>) =>
  String(rule.sensor_id ?? rule.value?.sensor_id ?? rule.value?.sensorId ?? rule.value?.source_sensor_id ?? '').trim();

const toEntityId = (value: unknown) => String(value ?? '').trim();

const sensorBelongsToKit = (sensor: AqlSensor, kit: AqlKit, kitDeviceIds = new Set<string>()) => {
  const sensorKitId = toEntityId(sensor.kit_id);
  const sensorZoneId = toEntityId(sensor.zone_id);
  const sensorDeviceId = toEntityId((sensor as Record<string, any>).device_id);

  return (
    sensorKitId === kit.kit_id ||
    sensorDeviceId === kit.kit_id ||
    (sensorDeviceId ? kitDeviceIds.has(sensorDeviceId) : false) ||
    Boolean(!sensorKitId && sensorZoneId && sensorZoneId === toEntityId(kit.zone_id))
  );
};

const sensorBelongsToProject = (
  sensor: AqlSensor,
  projectKitIds: Set<string>,
  projectZoneIds: Set<string>,
  projectDeviceIds: Set<string>
) => {
  const sensorKitId = toEntityId(sensor.kit_id);
  const sensorZoneId = toEntityId(sensor.zone_id);
  const sensorDeviceId = toEntityId((sensor as Record<string, any>).device_id);

  return (
    projectKitIds.has(sensorKitId) ||
    projectKitIds.has(sensorDeviceId) ||
    projectDeviceIds.has(sensorDeviceId) ||
    (sensorZoneId ? projectZoneIds.has(sensorZoneId) : false)
  );
};

const readStoredPositions = (storageKey: string) => {
  if (typeof window === 'undefined') return new Map<string, { x: number; y: number }>();
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) ?? '{}') as Record<string, { x: number; y: number }>;
    return new Map(
      Object.entries(parsed)
        .filter(([, position]) => Number.isFinite(position?.x) && Number.isFinite(position?.y))
        .map(([id, position]) => [id, position])
    );
  } catch {
    return new Map<string, { x: number; y: number }>();
  }
};

const writeStoredPositions = (storageKey: string, nodes: Node<ArchitectureGraphNodeData>[]) => {
  if (typeof window === 'undefined') return;
  const payload = nodes.reduce<Record<string, { x: number; y: number }>>((acc, node) => {
    acc[node.id] = node.position;
    return acc;
  }, {});
  window.localStorage.setItem(storageKey, JSON.stringify(payload));
};

const INITIAL_NODE_HANDLES = [
  { id: 'target-left', type: 'target' as const, position: Position.Left, x: 0, y: NODE_HEIGHT / 2 - 6, width: 12, height: 12 },
  { id: 'source-left', type: 'source' as const, position: Position.Left, x: 0, y: NODE_HEIGHT / 2 - 6, width: 12, height: 12 },
  { id: 'target-right', type: 'target' as const, position: Position.Right, x: NODE_WIDTH - 12, y: NODE_HEIGHT / 2 - 6, width: 12, height: 12 },
  { id: 'source-right', type: 'source' as const, position: Position.Right, x: NODE_WIDTH - 12, y: NODE_HEIGHT / 2 - 6, width: 12, height: 12 },
  { id: 'target-top', type: 'target' as const, position: Position.Top, x: NODE_WIDTH / 2 - 6, y: 0, width: 12, height: 12 },
  { id: 'source-top', type: 'source' as const, position: Position.Top, x: NODE_WIDTH / 2 - 6, y: 0, width: 12, height: 12 },
  { id: 'target-bottom', type: 'target' as const, position: Position.Bottom, x: NODE_WIDTH / 2 - 6, y: NODE_HEIGHT - 12, width: 12, height: 12 },
  { id: 'source-bottom', type: 'source' as const, position: Position.Bottom, x: NODE_WIDTH / 2 - 6, y: NODE_HEIGHT - 12, width: 12, height: 12 }
];

const makeNode = (
  id: string,
  nodeType: ArchitectureNodeType,
  position: { x: number; y: number },
  data: Omit<ArchitectureGraphNodeData, 'type'>,
  rfNodeType = 'architectureCard'
): Node<ArchitectureGraphNodeData> => ({
  id,
  type: rfNodeType,
  position,
  width: NODE_WIDTH,
  height: NODE_HEIGHT,
  handles: INITIAL_NODE_HANDLES,
  data: {
    ...data,
    type: nodeType
  }
});

const makeDraftNode = (nodeType: ArchitectureNodeType, position: { x: number; y: number }): Node<ArchitectureGraphNodeData> => {
  const titleMap: Record<ArchitectureNodeType, string> = {
    client: 'Novo cliente',
    project: 'Novo projeto',
    location: 'Nova instalação',
    zone: 'Nova área operacional',
    kit: 'Nova unidade edge',
    camera: 'Nova câmara',
    sensor: 'Novo sensor',
    ultrasound: 'Nova sonda',
    threshold: 'Nova regra'
  };

  return makeNode(`draft:${nodeType}:${Date.now()}`, nodeType, position, {
    title: titleMap[nodeType],
    subtitle: `${nodeTypeLabel[nodeType]} · Por configurar`,
    meta: 'Rascunho',
    status: 'novo',
    value: {
      __draft: true,
      type: nodeType
    }
  });
};

const makeEdge = (source: string, target: string, relation: ArchitectureRelation): Edge => ({
  id: `${source}->${target}:${relation}`,
  source,
  target,
  type: 'smoothstep',
  deletable: true,
  style: {
    stroke: '#38bdf8',
    strokeOpacity: 0.78,
    strokeWidth: 2
  },
  data: { relation }
});

const centerForNode = (node: Node<ArchitectureGraphNodeData>) => {
  const w = NODE_WIDTH;
  const h = NODE_HEIGHT;
  return { x: node.position.x + w / 2, y: node.position.y + h / 2 };
};

const bestHandlesForNodes = (sourceNode: Node<ArchitectureGraphNodeData>, targetNode: Node<ArchitectureGraphNodeData>) => {
  const sourceCenter = centerForNode(sourceNode);
  const targetCenter = centerForNode(targetNode);
  const dx = targetCenter.x - sourceCenter.x;
  const dy = targetCenter.y - sourceCenter.y;

  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx >= 0
      ? { sourceHandle: 'source-right', targetHandle: 'target-left' }
      : { sourceHandle: 'source-left', targetHandle: 'target-right' };
  }

  return dy >= 0
    ? { sourceHandle: 'source-bottom', targetHandle: 'target-top' }
    : { sourceHandle: 'source-top', targetHandle: 'target-bottom' };
};

const applyEdgeHandles = (nodes: Node<ArchitectureGraphNodeData>[], edges: Edge[]) => {
  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  const siblingGroupCounts = edges.reduce<Record<string, number>>((acc, edge) => {
    const targetNode = nodeMap.get(edge.target);
    if (!targetNode) return acc;
    const key = `${edge.source}:${targetNode.data.type}`;
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});

  return edges.map((edge) => {
    const sourceNode = nodeMap.get(edge.source);
    const targetNode = nodeMap.get(edge.target);
    if (!sourceNode || !targetNode) return edge;
    const siblingKey = `${edge.source}:${targetNode.data.type}`;
    if ((siblingGroupCounts[siblingKey] ?? 0) > 1) {
      return {
        ...edge,
        sourceHandle: 'source-bottom',
        targetHandle: 'target-top'
      };
    }
    return {
      ...edge,
      ...bestHandlesForNodes(sourceNode, targetNode)
    };
  });
};

const buildGraph = (
  clients: AqlClient[],
  dataset: AqlConfigurationDataset,
  projects: AqlProject[],
  rules: Array<Record<string, any>>,
  storedPositions: Map<string, { x: number; y: number }>
) => {
  const nodes: Node<ArchitectureGraphNodeData>[] = [];
  const edges: Edge[] = [];
  const positionFor = (id: string, fallback: { x: number; y: number }) => storedPositions.get(id) ?? fallback;

  if (!clients.length) return { nodes, edges };

  let globalYCursor = 0;

  clients.forEach((client) => {
    const clientNodeId = `client:${client.client_id}`;
    const directlyMatchedProjects = projects.filter((project) => project.client_id === client.client_id);
    const clientProjects = directlyMatchedProjects.length || clients.length > 1
      ? directlyMatchedProjects
      : projects;

    nodes.push(makeNode(clientNodeId, 'client', positionFor(clientNodeId, { x: LEVEL_X.client, y: globalYCursor }), {
      title: client.name,
      subtitle: client.slug ? `/${client.slug}` : 'Código por definir',
      meta: client.is_active ? 'Ativo' : 'Inativo',
        value: client
      }));

    let clientYCursor = globalYCursor;

    clientProjects.forEach((project) => {
      const contextProfile = getOperationalContextProfile(project.context_type);
      const projectNodeId = `project:${project.project_id}`;
      const projectY = clientYCursor;
      const projectLocations = dataset.locations.filter((location) =>
        location.project_id === project.project_id ||
        (!location.project_id && location.client_id === project.client_id)
      );
      const projectLocationIds = new Set(projectLocations.map((item) => item.location_id));
      const projectZones = dataset.zones.filter((zone) =>
        zone.project_id === project.project_id || projectLocationIds.has(zone.location_id)
      );
      const projectZoneIds = new Set(projectZones.map((item) => item.zone_id));
      const projectKits = dataset.kits.filter((kit) =>
        kit.project_id === project.project_id ||
        projectLocationIds.has(kit.location_id) ||
        (kit.zone_id ? projectZoneIds.has(kit.zone_id) : false)
      );
      const projectKitIds = new Set(projectKits.map((item) => item.kit_id));
      const projectCameras = dataset.cameras.filter((camera) =>
        (camera.kit_id ? projectKitIds.has(camera.kit_id) : false) ||
        (camera.zone_id ? projectZoneIds.has(camera.zone_id) : false)
      );
      const projectUltrasound = dataset.ultrasoundDevices.filter((device) =>
        projectKitIds.has(device.kit_id)
      );
      const projectDeviceIds = new Set(projectUltrasound.map((device) => device.device_id));
      const projectSensors = dataset.sensors.filter((sensor) =>
        sensorBelongsToProject(sensor, projectKitIds, projectZoneIds, projectDeviceIds)
      );
      const projectRules = rules.filter((rule) => rule.project_id === project.project_id || rule.client_id === project.client_id);
      const placedRuleIds = new Set<string>();

      nodes.push(makeNode(projectNodeId, 'project', positionFor(projectNodeId, { x: LEVEL_X.project, y: projectY }), {
        title: project.name,
        subtitle: [project.species_focus, project.default_phase].filter(Boolean).join(' · ') || contextProfile.categoryPhaseFallback,
        meta: `${formatCount(projectLocations.length, 'instalação', 'instalações')} · ${formatCount(projectKits.length, 'unidade', 'unidades')}`,
        value: project
      }));
      edges.push(makeEdge(clientNodeId, projectNodeId, 'owns'));

    projectLocations.forEach((location, locationIndex) => {
      const locationNodeId = `location:${location.location_id}`;
      const locationY = projectY + locationIndex * 280;
      nodes.push(makeNode(locationNodeId, 'location', positionFor(locationNodeId, { x: LEVEL_X.location, y: locationY }), {
        title: location.name,
        subtitle: [location.city, location.country].filter(Boolean).join(', ') || getOperationalLocationTypeLabel(project.context_type, location.type) || 'Localização por definir',
        meta: location.latitude != null && location.longitude != null ? 'Georreferenciada' : 'Sem coordenadas',
        value: location
      }));
      edges.push(makeEdge(projectNodeId, locationNodeId, 'contains'));

      const locationZones = projectZones.filter((zone) => zone.location_id === location.location_id);
      locationZones.forEach((zone, zoneIndex) => {
        const zoneNodeId = `zone:${zone.zone_id}`;
        const zoneY = locationY + zoneIndex * 180;

        nodes.push(makeNode(zoneNodeId, 'zone', positionFor(zoneNodeId, { x: LEVEL_X.zone, y: zoneY }), {
          title: zone.name,
          subtitle: [zone.species, zone.phase].filter(Boolean).join(' · ') || zone.type || `${contextProfile.areaLabel} por caracterizar`,
          meta: [
            zone.code ? `Código ${zone.code}` : 'Sem código operacional',
            zone.latitude != null && zone.longitude != null ? 'Georreferenciada' : null
          ].filter(Boolean).join(' · '),
          value: zone
        }));
        edges.push(makeEdge(locationNodeId, zoneNodeId, 'contains'));

        const zoneKits = projectKits.filter((kit) => kit.zone_id === zone.zone_id);
        zoneKits.forEach((kit, kitIndex) => {
          const kitNodeId = `kit:${kit.kit_id}`;
          const kitY = zoneY + kitIndex * 180;
          nodes.push(makeNode(kitNodeId, 'kit', positionFor(kitNodeId, { x: LEVEL_X.kit, y: kitY }), {
            title: kit.name,
            subtitle: kit.kit_type || 'Tipo por definir',
            meta: kit.device_uid || kit.serial_number || 'Identificador por definir',
            value: kit
          }));
          edges.push(makeEdge(zoneNodeId, kitNodeId, 'monitors'));

          const kitDevices: Array<{
            id: string;
            type: ArchitectureNodeType;
            y: number;
            title: string;
            subtitle: string;
            meta?: string;
            value: Record<string, any>;
          }> = [
            ...projectCameras.filter((camera) => camera.kit_id === kit.kit_id).map((camera) => ({
              id: `camera:${camera.camera_id}`,
              type: 'camera' as const,
              y: 0,
              title: camera.name || camera.camera_code || 'Câmara sem nome',
              subtitle: camera.live_feed_path || camera.source_type || 'Feed por definir',
              meta: camera.camera_code || 'Código por definir',
              value: camera
            })),
            ...projectSensors.filter((sensor) => {
              const kitDeviceIds = new Set(projectUltrasound
                .filter((device) => device.kit_id === kit.kit_id)
                .map((device) => device.device_id));
              return sensorBelongsToKit(sensor, kit, kitDeviceIds);
            }).map((sensor) => {
              const normalizedType = String(sensor.sensor_type ?? '').toLowerCase();
              return {
                id: `sensor:${sensor.sensor_id}`,
                type: 'sensor' as const,
                y: 0,
                title: sensor.name || sensorLabelMap[normalizedType] || 'Sensor',
                subtitle: sensorLabelMap[normalizedType] || sensor.sensor_type || 'Sensor',
                meta: sensor.unit ? `Unidade ${sensor.unit}` : 'Unidade por definir',
                value: sensor
              };
            }),
            ...projectUltrasound.filter((device) => device.kit_id === kit.kit_id).map((device) => ({
              id: `ultrasound:${device.device_id}`,
              type: 'ultrasound' as const,
              y: 0,
              title: device.name,
              subtitle: device.description || 'Sonda associada à unidade',
              meta: device.device_uid || 'Identificador por definir',
              value: device
            }))
          ];

          kitDevices.forEach((device, deviceIndex) => {
            const relation: ArchitectureRelation =
              device.type === 'camera' ? 'camera_feed' :
              device.type === 'sensor' ? 'sensor_feed' :
              'probe_feed';
            nodes.push(makeNode(device.id, device.type, positionFor(device.id, { x: LEVEL_X.device, y: kitY + deviceIndex * 130 }), {
              title: device.title,
              subtitle: device.subtitle,
              meta: device.meta,
              value: device.value
            }));
            edges.push(makeEdge(kitNodeId, device.id, relation));

            if (device.type === 'sensor') {
              const sensorRules = projectRules.filter((rule) => ruleSensorId(rule) === device.value.sensor_id);
              sensorRules.forEach((rule, ruleIndex) => {
                const ruleNodeId = `threshold:${rule.setting_id}`;
                placedRuleIds.add(rule.setting_id);
                nodes.push(makeNode(ruleNodeId, 'threshold', positionFor(ruleNodeId, { x: LEVEL_X.rule, y: kitY + deviceIndex * 130 + ruleIndex * 130 }), {
                  title: rule.key || 'Regra sem chave',
                  subtitle: device.title,
                  meta: 'Aplicada ao sensor',
                  value: rule
                }));
                edges.push(makeEdge(device.id, ruleNodeId, 'alert_rule'));
              });
            }
          });
        });
      });
    });

    projectRules.filter((rule) => !placedRuleIds.has(rule.setting_id)).forEach((rule, ruleIndex) => {
      const sensorId = ruleSensorId(rule);
      const ruleNodeId = `threshold:${rule.setting_id}`;
      nodes.push(makeNode(ruleNodeId, 'threshold', positionFor(ruleNodeId, { x: LEVEL_X.rule, y: projectY + 520 + ruleIndex * 130 }), {
        title: rule.key || 'Regra sem chave',
        subtitle: sensorId ? 'Sensor não encontrado' : 'Sensor por associar',
        meta: 'Requer associação a um sensor',
        value: rule
      }));
      edges.push(makeEdge(projectNodeId, ruleNodeId, 'alert_rule'));
    });

      clientYCursor += Math.max(360, projectLocations.length * 300, projectKits.length * 180);
    });

    globalYCursor = clientYCursor + 260;
  });

  return { nodes, edges: applyEdgeHandles(nodes, edges) };
};

const relationForConnection = (sourceType: ArchitectureNodeType, targetType: ArchitectureNodeType): ArchitectureRelation | null => {
  if (sourceType === 'client' && targetType === 'project') return 'owns';
  if (sourceType === 'project' && targetType === 'location') return 'contains';
  if (sourceType === 'location' && targetType === 'zone') return 'contains';
  if (sourceType === 'zone' && targetType === 'kit') return 'monitors';
  if (sourceType === 'kit' && targetType === 'camera') return 'camera_feed';
  if (sourceType === 'kit' && targetType === 'sensor') return 'sensor_feed';
  if (sourceType === 'kit' && targetType === 'ultrasound') return 'probe_feed';
  if (sourceType === 'sensor' && targetType === 'threshold') return 'alert_rule';
  return null;
};

const handleClassName = '!h-3 !w-3 !border-2 !border-white !bg-sky-500 !shadow-sm';

function ArchitectureCardNode({ data, selected }: NodeProps<Node<ArchitectureGraphNodeData>>) {
  const Icon = data.type === 'sensor'
    ? sensorIconFor(String(data.value?.sensor_type ?? ''))
    : nodeIconMap[data.type];

  return (
    <div
      className={`relative w-[300px] rounded-xl border p-3 pr-4 text-left shadow-[0_18px_44px_-32px_rgba(15,23,42,0.8)] transition-shadow overflow-hidden ${nodeToneClass[data.type]} ${selected ? 'ring-2 ring-primary/45' : ''}`}
    >
      <Handle id="target-left" type="target" position={Position.Left} className={handleClassName} />
      <Handle id="source-left" type="source" position={Position.Left} className={handleClassName} />
      <Handle id="target-right" type="target" position={Position.Right} className={handleClassName} />
      <Handle id="source-right" type="source" position={Position.Right} className={handleClassName} />
      <Handle id="target-top" type="target" position={Position.Top} className={handleClassName} />
      <Handle id="source-top" type="source" position={Position.Top} className={handleClassName} />
      <Handle id="target-bottom" type="target" position={Position.Bottom} className={handleClassName} />
      <Handle id="source-bottom" type="source" position={Position.Bottom} className={handleClassName} />
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-current/15 bg-white/55 text-current shadow-sm dark:bg-white/10 dark:shadow-black/20">
            <Icon className="h-4 w-4" />
          </span>
          <span className="min-w-0">
            <span className="block text-[9px] font-bold uppercase tracking-[0.16em] text-current/55">{nodeTypeLabel[data.type]}</span>
            <span className="mt-0.5 block truncate text-sm font-bold text-current">{data.title}</span>
            {data.subtitle ? <span className="mt-0.5 block truncate text-xs font-medium text-current/62">{data.subtitle}</span> : null}
          </span>
        </div>
      </div>
      {data.meta ? (
        <div className="mt-3 border-t border-current/10 pt-2 text-[11px] font-semibold leading-4 text-current/62">
          {data.meta}
        </div>
      ) : null}
    </div>
  );
}

const nodeTypes = {
  architectureCard: ArchitectureCardNode
};

export function OperationalArchitectureFlow({ client, clients, allowClientCreation = false, discardDraftNodeId = null, dataset, projects, rules, onOpenNode }: OperationalArchitectureFlowProps) {
  const graphClients = useMemo(() => clients?.length ? clients : client ? [client] : [], [client, clients]);
  const visiblePaletteNodeTypes = useMemo(
    () => paletteNodeTypes.filter((item) => allowClientCreation || item.type !== 'client'),
    [allowClientCreation]
  );
  const storageKey = storageKeyForClient(graphClients.length === 1 ? graphClients[0]?.client_id ?? null : null);
  const [flowInstance, setFlowInstance] = useState<ReactFlowInstance<ArchitectureGraphNodeData> | null>(null);
  const flowInstanceRef = useRef<ReactFlowInstance<ArchitectureGraphNodeData> | null>(null);
  const initialGraph = useMemo(
    () => buildGraph(graphClients, dataset, projects, rules, readStoredPositions(storageKey)),
    [graphClients, dataset, projects, rules, storageKey]
  );
  const [nodes, setNodes] = useNodesState<ArchitectureGraphNodeData>(initialGraph.nodes);
  const [edges, setEdges, onEdgesChangeBase] = useEdgesState(initialGraph.edges);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [toolbarHidden, setToolbarHidden] = useState(false);
  const flowContainerRef = useRef<HTMLDivElement | null>(null);
  const paletteDragTypeRef = useRef<ArchitectureNodeType | null>(null);
  const palettePointerRef = useRef<{ type: ArchitectureNodeType; startX: number; startY: number; moved: boolean } | null>(null);
  const suppressPaletteClickRef = useRef(false);
  const previousGraphNodeIdsRef = useRef<Set<string>>(new Set(initialGraph.nodes.map((node) => node.id)));
  const hasFittedInitialGraphRef = useRef(false);

  useEffect(() => {
    const previousGraphNodeIds = previousGraphNodeIdsRef.current;
    const hasNewGraphNodes = initialGraph.nodes.some((node) => !previousGraphNodeIds.has(node.id));
    previousGraphNodeIdsRef.current = new Set(initialGraph.nodes.map((node) => node.id));

    setNodes((currentNodes) => {
      const currentPositionMap = new Map(currentNodes.map((node) => [node.id, node.position]));
      const nextNodes = initialGraph.nodes.map((node) => ({
        ...node,
        position: currentPositionMap.get(node.id) ?? node.position
      }));
      return nextNodes;
    });
    setEdges(initialGraph.edges);

    const shouldFitGraph = initialGraph.nodes.length > 0 && (
      hasNewGraphNodes || !hasFittedInitialGraphRef.current
    );
    if (shouldFitGraph && flowInstance) {
      hasFittedInitialGraphRef.current = true;
      requestAnimationFrame(() => {
        flowInstance.fitView({ padding: 0.12, duration: 420, minZoom: READABLE_MIN_ZOOM, maxZoom: 0.86 });
      });
    }
  }, [flowInstance, initialGraph, setEdges, setNodes]);

  useEffect(() => {
    writeStoredPositions(storageKey, nodes);
  }, [nodes, storageKey]);

  useEffect(() => {
    if (!discardDraftNodeId) return;
    setNodes((currentNodes) => currentNodes.filter((node) => node.id !== discardDraftNodeId));
  }, [discardDraftNodeId, setNodes]);

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setNodes((currentNodes) => {
      const nextNodes = applyNodeChanges(changes, currentNodes) as Node<ArchitectureGraphNodeData>[];
      setEdges((currentEdges) => applyEdgeHandles(nextNodes, currentEdges));
      return nextNodes;
    });
  }, [setEdges, setNodes]);

  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    onEdgesChangeBase(changes);
  }, [onEdgesChangeBase]);

  const onConnect = useCallback((connection: Connection) => {
    if (!connection.source || !connection.target || connection.source === connection.target) return;
    const sourceNode = nodes.find((node) => node.id === connection.source);
    const targetNode = nodes.find((node) => node.id === connection.target);
    if (!sourceNode || !targetNode) return;

    const relation = relationForConnection(sourceNode.data.type, targetNode.data.type);
    if (!relation) return;

    setEdges((currentEdges) => {
      const nextEdge = applyEdgeHandles(nodes, [makeEdge(connection.source!, connection.target!, relation)])[0];
      if (currentEdges.some((edge) => edge.id === nextEdge.id)) return currentEdges;
      return addEdge(nextEdge, currentEdges);
    });
  }, [nodes, setEdges]);

  const removeSelectedEdge = () => {
    if (!selectedEdgeId) return;
    setEdges((currentEdges) => currentEdges.filter((edge) => edge.id !== selectedEdgeId));
    setSelectedEdgeId(null);
  };
  const fitReadableView = useCallback(() => {
    flowInstance?.fitView({ padding: 0.12, duration: 420, minZoom: READABLE_MIN_ZOOM, maxZoom: 0.86 });
  }, [flowInstance]);
  const hideToolbar = useCallback(() => setToolbarHidden(true), []);
  const showToolbar = useCallback(() => setToolbarHidden(false), []);
  const handleFlowInit = useCallback((instance: ReactFlowInstance<ArchitectureGraphNodeData>) => {
    flowInstanceRef.current = instance;
    setFlowInstance(instance);
  }, []);

  const handlePaletteDragStart = (event: DragEvent<HTMLButtonElement>, nodeType: ArchitectureNodeType) => {
    paletteDragTypeRef.current = nodeType;
    event.dataTransfer.setData('application/aql-node-type', nodeType);
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.setData('text/plain', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  const handlePaletteDragEnd = () => {
    paletteDragTypeRef.current = null;
  };

  const flowPositionForScreenPoint = useCallback((x: number, y: number) => {
    const activeFlowInstance = flowInstanceRef.current ?? flowInstance;
    if (activeFlowInstance) return activeFlowInstance.screenToFlowPosition({ x, y });

    const container = flowContainerRef.current;
    const viewport = container?.querySelector<HTMLElement>('.react-flow__viewport');
    if (!container || !viewport) return null;
    const bounds = container.getBoundingClientRect();
    const transform = window.getComputedStyle(viewport).transform;
    const matrix = transform && transform !== 'none' ? new DOMMatrixReadOnly(transform) : new DOMMatrixReadOnly();
    const zoom = matrix.a || 1;
    return {
      x: (x - bounds.left - matrix.e) / zoom,
      y: (y - bounds.top - matrix.f) / zoom
    };
  }, [flowInstance]);

  const createDraftAtScreenPoint = useCallback((nodeType: ArchitectureNodeType, x: number, y: number) => {
    const flowPoint = flowPositionForScreenPoint(x, y);
    if (!flowPoint) return;
    const nodeElement = document.elementFromPoint(x, y)?.closest<HTMLElement>('.react-flow__node[data-id]');
    const parentNode = nodeElement
      ? nodes.find((node) => node.id === nodeElement.dataset.id) ?? null
      : null;
    const relation = parentNode ? relationForConnection(parentNode.data.type, nodeType) : null;
    const draftNode = makeDraftNode(nodeType, parentNode && relation
      ? {
          x: parentNode.position.x + NODE_WIDTH + 80,
          y: parentNode.position.y
        }
      : {
          x: flowPoint.x - NODE_WIDTH / 2,
          y: flowPoint.y - NODE_HEIGHT / 2
        });
    if (parentNode && relation) {
      draftNode.data.value = {
        ...draftNode.data.value,
        ...parentNode.data.value,
        __parent_node_id: parentNode.id,
        __parent_type: parentNode.data.type
      };
    }
    setNodes((currentNodes) => [...currentNodes, draftNode]);
    if (parentNode && relation) {
      setEdges((currentEdges) => [...currentEdges, makeEdge(parentNode.id, draftNode.id, relation)]);
    }
    onOpenNode(nodeType, draftNode.data.value, { draftNodeId: draftNode.id });
  }, [flowPositionForScreenPoint, nodes, onOpenNode, setEdges, setNodes]);

  const handlePalettePointerDown = (event: ReactPointerEvent<HTMLButtonElement>, nodeType: ArchitectureNodeType) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    palettePointerRef.current = { type: nodeType, startX: event.clientX, startY: event.clientY, moved: false };
  };

  const handlePalettePointerMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const active = palettePointerRef.current;
    if (!active) return;
    if (Math.hypot(event.clientX - active.startX, event.clientY - active.startY) > 6) active.moved = true;
  };

  const handlePalettePointerUp = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const active = palettePointerRef.current;
    if (!active) return;
    event.stopPropagation();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    palettePointerRef.current = null;
    if (active.moved) {
      suppressPaletteClickRef.current = true;
      createDraftAtScreenPoint(active.type, event.clientX, event.clientY);
    }
  };

  const handlePaletteClick = (nodeType: ArchitectureNodeType) => {
    if (suppressPaletteClickRef.current) {
      suppressPaletteClickRef.current = false;
      return;
    }
    const container = flowContainerRef.current;
    const bounds = container?.getBoundingClientRect();
    if (!container || !bounds) return;

    const containerCenter = {
      x: bounds.left + bounds.width / 2,
      y: bounds.top + bounds.height / 2
    };
    const compatibleParents = nodes
      .filter((node) => Boolean(relationForConnection(node.data.type, nodeType)))
      .map((node) => {
        const element = container.querySelector<HTMLElement>(`.react-flow__node[data-id="${CSS.escape(node.id)}"]`);
        if (!element) return null;
        const rect = element.getBoundingClientRect();
        const point = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
        const isVisible = point.x >= bounds.left && point.x <= bounds.right && point.y >= bounds.top && point.y <= bounds.bottom;
        return isVisible
          ? { point, distance: Math.hypot(point.x - containerCenter.x, point.y - containerCenter.y) }
          : null;
      })
      .filter((candidate): candidate is { point: { x: number; y: number }; distance: number } => Boolean(candidate))
      .sort((a, b) => a.distance - b.distance);

    const targetPoint = compatibleParents[0]?.point ?? containerCenter;
    createDraftAtScreenPoint(nodeType, targetPoint.x, targetPoint.y);
  };

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const nodeType = (
      event.dataTransfer.getData('application/aql-node-type') ||
      event.dataTransfer.getData('application/reactflow') ||
      event.dataTransfer.getData('text/plain') ||
      paletteDragTypeRef.current ||
      ''
    ) as ArchitectureNodeType;
    const activeFlowInstance = flowInstanceRef.current ?? flowInstance;
    if (!nodeType || !visiblePaletteNodeTypes.some((item) => item.type === nodeType) || !activeFlowInstance) return;

    const position = activeFlowInstance.screenToFlowPosition({
      x: event.clientX,
      y: event.clientY
    });
    const draftNode = makeDraftNode(nodeType, position);
    setNodes((currentNodes) => [...currentNodes, draftNode]);
    paletteDragTypeRef.current = null;
    onOpenNode(nodeType, draftNode.data.value, { draftNodeId: draftNode.id });
  }, [flowInstance, onOpenNode, setNodes, visiblePaletteNodeTypes]);

  return (
    <div ref={flowContainerRef} className="aql-operational-flow relative h-[calc(100dvh-6.5rem)] min-h-[620px] overflow-hidden rounded-2xl border border-border bg-card dark:bg-slate-950/70">
      <style>
        {`
          .aql-operational-flow .react-flow__pane {
            cursor: grab;
          }

          .aql-operational-flow .react-flow__pane:active {
            cursor: grabbing;
          }

          .aql-operational-flow .react-flow__controls {
            overflow: hidden;
            border: 1px solid hsl(var(--border));
            border-radius: 12px;
            box-shadow: 0 18px 40px -28px rgba(2, 6, 23, 0.65);
          }

          .aql-operational-flow .react-flow__controls-button {
            width: 34px;
            height: 34px;
            border-bottom: 1px solid hsl(var(--border));
            background: hsl(var(--card) / 0.94);
            color: hsl(var(--foreground));
          }

          .aql-operational-flow .react-flow__controls-button:hover {
            background: hsl(var(--muted));
          }

          .aql-operational-flow .react-flow__minimap {
            overflow: hidden;
            border: 1px solid hsl(var(--border));
            border-radius: 12px;
            background: hsl(var(--card) / 0.92);
            box-shadow: 0 18px 40px -28px rgba(2, 6, 23, 0.65);
          }

          .dark .aql-operational-flow .react-flow__controls,
          .dark .aql-operational-flow .react-flow__minimap {
            border-color: rgba(148, 163, 184, 0.28);
            box-shadow: 0 18px 54px -30px rgba(0, 0, 0, 0.85);
          }

          .dark .aql-operational-flow .react-flow__controls-button {
            border-bottom-color: rgba(148, 163, 184, 0.22);
            background: rgba(15, 23, 42, 0.92);
            color: rgb(226, 232, 240);
          }

          .dark .aql-operational-flow .react-flow__controls-button:hover {
            background: rgba(30, 41, 59, 0.96);
          }

          .aql-operational-flow .react-flow__selection {
            border: 1px solid rgb(14, 165, 233);
            background: rgba(14, 165, 233, 0.12);
          }

          .dark .aql-operational-flow .react-flow__selection {
            border-color: rgb(56, 189, 248);
            background: rgba(56, 189, 248, 0.16);
          }
        `}
      </style>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onInit={handleFlowInit}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onMoveStart={hideToolbar}
        onMoveEnd={showToolbar}
        onNodeDragStart={hideToolbar}
        onNodeDragStop={showToolbar}
        onSelectionDragStart={hideToolbar}
        onSelectionDragStop={showToolbar}
        onNodeClick={(event, node) => {
          if (event.shiftKey || event.metaKey || event.ctrlKey) return;
          onOpenNode(node.data.type, node.data.value);
        }}
        onNodeDoubleClick={(event, node) => {
          if (event.shiftKey || event.metaKey || event.ctrlKey) return;
          onOpenNode(node.data.type, node.data.value);
        }}
        onEdgeClick={(_, edge) => setSelectedEdgeId(edge.id)}
        defaultViewport={READABLE_INITIAL_VIEWPORT}
        minZoom={READABLE_MIN_ZOOM}
        maxZoom={READABLE_MAX_ZOOM}
        nodesDraggable
        nodesConnectable
        edgesFocusable
        elementsSelectable
        selectionKeyCode="Shift"
        multiSelectionKeyCode={['Shift', 'Meta', 'Control']}
        selectionMode={SelectionMode.Partial}
        autoPanOnSelection
        deleteKeyCode={['Backspace', 'Delete']}
        proOptions={{ hideAttribution: true }}
      >
        <Background className="dark:opacity-45" color="rgba(14,165,233,0.24)" gap={22} size={1.2} />
        <Controls position="bottom-left" />
        <MiniMap
          position="bottom-right"
          pannable
          zoomable
          bgColor="#ffffff"
          maskColor="rgba(248, 250, 252, 0.72)"
          maskStrokeColor="#cbd5e1"
          nodeColor={(node) => {
            const type = (node.data as ArchitectureGraphNodeData).type;
            if (type === 'kit') return '#fb923c';
            if (type === 'location') return '#34d399';
            if (type === 'zone') return '#818cf8';
            if (type === 'sensor') return '#60a5fa';
            if (type === 'camera') return '#a78bfa';
            if (type === 'threshold') return '#fb7185';
            return '#38bdf8';
          }}
        />
        <button
          type="button"
          onClick={fitReadableView}
          className="absolute bottom-5 left-16 z-10 inline-flex h-9 items-center gap-2 rounded-xl border border-border bg-card/94 px-3 text-xs font-bold text-foreground shadow-sm backdrop-blur transition-colors hover:bg-muted"
          title="Recentrar arquitetura"
        >
          <Maximize2 className="h-3.5 w-3.5 text-primary" />
          <span className="hidden sm:inline">Recentrar</span>
        </button>
        <div className={`absolute left-1/2 top-4 z-10 flex -translate-x-1/2 items-center gap-2 rounded-2xl border border-border bg-card/92 px-3 py-2 shadow-lg backdrop-blur transition-all duration-300 ease-out ${
          toolbarHidden
            ? 'pointer-events-none -translate-y-3 scale-95 opacity-0'
            : 'translate-y-0 scale-100 opacity-100'
        }`}>
          {visiblePaletteNodeTypes.map((item) => {
            const Icon = nodeIconMap[item.type];
            return (
              <button
                key={item.type}
                type="button"
                onPointerDown={(event) => handlePalettePointerDown(event, item.type)}
                onPointerMove={handlePalettePointerMove}
                onPointerUp={handlePalettePointerUp}
                onPointerCancel={() => { palettePointerRef.current = null; }}
                onClick={() => handlePaletteClick(item.type)}
                className="nodrag nopan flex min-w-[58px] cursor-grab flex-col items-center gap-1 rounded-xl px-2 py-1.5 text-xs font-bold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground active:cursor-grabbing"
                title={`Arrastar ou clicar para criar ${item.label}`}
              >
                <span className={`flex h-9 w-9 items-center justify-center rounded-xl border shadow-sm dark:shadow-black/20 ${nodeToneClass[item.type]}`}>
                  <Icon className="h-4 w-4" />
                </span>
                <span className="hidden sm:block">{item.label}</span>
              </button>
            );
          })}
        </div>
        {nodes.length === 0 ? (
          <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center">
            <div className="max-w-sm rounded-2xl border border-dashed border-border bg-card/82 px-5 py-4 text-center shadow-sm backdrop-blur">
              <p className="text-sm font-bold text-foreground">Canvas vazio</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Arrasta objetos da barra superior para começar a montar a configuração visual.
              </p>
            </div>
          </div>
        ) : null}
        {selectedEdgeId ? (
          <button
            type="button"
            onClick={removeSelectedEdge}
            className="absolute right-5 top-5 z-10 rounded-xl border border-border bg-card px-3 py-2 text-xs font-bold text-foreground shadow-sm transition-colors hover:bg-muted"
          >
            Remover ligação
          </button>
        ) : null}
      </ReactFlow>
    </div>
  );
}
